/**
 * Something is happening and we don't know how long it takes (cockatrice-ui `src/ui/Spinner.jsx`, ported to
 * hyperscript; PLAN.md §0). Prefer ProgressBar when you do.
 * The label is always read by screen readers; showLabel also prints it beside the spinner.
 */
import './css/spinner.css';
import { el, when } from './solid.ts';
import type { Child } from './solid.ts';
import { uiText } from './text.ts';

export interface SpinnerProps {
  /** 16, 20 or 32 px. Default "md". */
  readonly size?: 'sm' | 'md' | 'lg' | undefined;
  /** Read by screen readers. Default "Loading". */
  readonly label?: string | undefined;
  /** Also print the label beside the spinner. */
  readonly showLabel?: boolean | undefined;
  /** Extra class. */
  readonly class?: string | undefined;
}

const PIXELS = { sm: 16, md: 20, lg: 32 } as const;

export default function Spinner(props: SpinnerProps): Child {
  const px = (): number => PIXELS[props.size ?? 'md'];
  const label = (): string => props.label ?? uiText()('ui.spinner.label');
  return el(
    'span',
    {
      class: () => `ui-spinner ui-spinner--${props.size ?? 'md'}${props.class !== undefined && props.class !== '' ? ` ${props.class}` : ''}`,
      role: 'status',
    },
    el(
      'svg',
      { class: 'ui-spinner__svg', width: px, height: px, viewBox: '0 0 24 24', 'aria-hidden': 'true', focusable: 'false' },
      el('circle', { class: 'ui-spinner__track', cx: '12', cy: '12', r: '9', fill: 'none', 'stroke-width': '2.5' }),
      el('circle', {
        class: 'ui-spinner__arc',
        cx: '12',
        cy: '12',
        r: '9',
        fill: 'none',
        'stroke-width': '2.5',
        'stroke-linecap': 'round',
        'stroke-dasharray': '16 60',
      }),
    ),
    when(
      () => props.showLabel === true,
      () => el('span', { class: 'ui-spinner__label' }, label),
      () => el('span', { class: 'sr-only' }, label),
    ),
  );
}
